import React from 'react';
import { Col, Form, Container, Row } from 'reactstrap';
import { UseFormReturn } from '@/hooks/useForm';
import { Button, InputText, Loading, SelectCustom } from '@/components';
import { ReactComponent as IdCard } from '@/assets/images/svg/idCard.svg';
import { ReactComponent as LoginLock } from '@/assets/images/svg/loginLock.svg';
import logoBanca from '@/assets/images/logo/logo-banca.png';
import eye from '@/assets/images/login/eye.png';
import closeEye from '@/assets/images/login/closeEye.png';
import { UserTypeOptions } from '..';
// import './styles.scss';

// eslint-disable-next-line no-shadow
export enum States {
  default = 'default',
  loading = 'loading',
  error = 'error',
}

interface LoginContainerProps
  extends Pick<UseFormReturn, 'formData' | 'formErrors' | 'onChangeFormInput'> {
  state: States;
  types: UserTypeOptions[];
  shouldShowPasswordToText: boolean;
  onCheckUserType: (cpf: string) => void;
  onTogglePasswordToText: () => void;
  onSubmit: () => void;
  onGoToForgotPassword: () => void;
}

// eslint-disable-next-line no-shadow
export enum FormInputName {
  document = 'document',
  userType = 'userType',
  password = 'password',
}

export const LoginContainer: React.FC<LoginContainerProps> = ({
  state,
  shouldShowPasswordToText,
  formData,
  formErrors,
  types,
  onChangeFormInput,
  onCheckUserType,
  onSubmit,
  onTogglePasswordToText,
  onGoToForgotPassword,
}) => {
  const shouldShowUserType = types && types.length > 1;

  const isSubmitDisabled =
    !formData[FormInputName.document] ||
    !formData[FormInputName.password] ||
    (shouldShowUserType && !formData[FormInputName.userType]) ||
    state === States.loading;

  return (
    <>
      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: radial-gradient(circle at top left, rgba(27, 72, 128, 0.1), transparent 35%),
            radial-gradient(circle at bottom right, rgba(27, 72, 128, 0.12), transparent 30%),
            linear-gradient(135deg, #f5f9ff 0%, #eef4fb 45%, #f8fbff 100%);
        }

        .login-container {
          max-width: 560px;
        }

        .login-card-modern {
          width: 100%;
          background: #ffffff;
          border-radius: 28px;
          padding: 48px 36px;
          box-shadow: 0 20px 60px rgba(27, 72, 128, 0.12);
          border: 1px solid rgba(27, 72, 128, 0.08);
        }

        .login-header {
          display: flex;
          justify-content: center;
          margin-bottom: 28px;
        }

        .login-logo {
          max-width: 140px;
          height: auto;
        }

        .login-form-modern {
          width: 100%;
        }

        .login-title {
          margin: 0;
          text-align: center;
          font-size: 2rem;
          line-height: 1.2;
          font-weight: 800;
          color: #111827;
        }

        .login-subtitle {
          margin: 8px 0 6px;
          text-align: center;
          font-size: 1.75rem;
          line-height: 1.2;
          font-weight: 800;
          color: #1b4880;
        }

        .login-description {
          display: block;
          text-align: center;
          color: #6b7280;
          font-size: 1rem;
          margin-bottom: 32px;
        }

        .login-divider {
          margin: 0 0 28px 0;
          border: 0;
          border-top: 1px solid #e5e7eb;
        }

        .login-fields {
          display: flex;
          flex-direction: column;
          gap: 18px;
          margin-bottom: 28px;
        }

        .login-field-row {
          margin-bottom: 0 !important;
        }

        .login-label {
          display: flex;
          align-items: center;
          font-weight: 600;
          color: #374151;
        }

        .login-label svg,
        .login-label img {
          width: 18px;
          height: 18px;
          object-fit: contain;
          flex-shrink: 0;
        }

        .password-toggle {
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .passwordIcon {
          width: 20px;
          height: 20px;
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }

        .password-toggle:hover .passwordIcon {
          opacity: 1;
        }

        .login-button-modern {
          width: 100% !important;
        }

        .login-button-modern.btn,
        .login-button-modern {
          background: #1b4880 !important;
          color: #ffffff !important;
          border: none !important;
          border-radius: 14px !important;
          box-shadow: 0 10px 24px rgba(27, 72, 128, 0.22) !important;
          transition: all 0.2s ease-in-out;
          font-weight: 700 !important;
        }

        .login-button-modern.button-xl {
          min-height: 56px !important;
          padding: 16px 0 !important;
          font-size: 1rem !important;
          line-height: 24px !important;
        }

        .login-button-modern:hover:not(:disabled) {
          background: #163c69 !important;
          transform: translateY(-1px);
          box-shadow: 0 14px 28px rgba(27, 72, 128, 0.28) !important;
        }

        .login-button-modern:disabled {
          background: #cfd8e3 !important;
          color: #ffffff !important;
          box-shadow: none !important;
          cursor: not-allowed !important;
        }

        .forgotPassword {
          margin-top: 22px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .forgotPasswordLink {
          background: none;
          border: none;
          padding: 0;
          color: #1b4880 !important;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .forgotPasswordLink:hover {
          color: #163c69 !important;
          text-decoration: underline;
        }

        @media (max-width: 576px) {
          .login-page {
            padding: 16px;
          }

          .login-card-modern {
            padding: 32px 22px;
            border-radius: 22px;
          }

          .login-title {
            font-size: 1.6rem;
          }

          .login-subtitle {
            font-size: 1.35rem;
          }

          .login-description {
            font-size: 0.95rem;
          }
        }

        .password-input-wrapper {
          position: relative;
          width: 100%;
        }

        .password-input-wrapper input {
          padding-right: 48px !important;
        }

        .password-toggle-inside .passwordIcon {
          width: 20px;
          height: 20px;
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }

        .password-toggle-inside:hover .passwordIcon {
          opacity: 1;
        }

        .password-toggle-inside {
          position: absolute;
          right: 16px;
          top: 40px;
          transform: translateY(-50%);
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
        }

        .password-toggle-inside .passwordIcon {
          width: 26px;
          height: 26px;
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }

        .password-toggle-inside:hover .passwordIcon {
          opacity: 1;
        }

        .password-input-wrapper input {
          padding-right: 48px !important;
        }

      `}</style>

      <Loading isVisible={state === States.loading} />

      <div className="login-page">
        <Container className="login-container">
          {/* <Row className="justify-content-center"> */}
          <Col className="login-card-modern">
            <div className="login-header">
              <img className="login-logo" src={logoBanca} alt="login page" />
            </div>

            <Form
              className="login-form-modern"
              noValidate
              onSubmit={(e): void => {
                e.preventDefault();
                onSubmit();
              }}
            >
              <Row className="login-field-row">
                <Col>
                  <h1 className="login-title">Seja bem-vindo(a)!</h1>
                  <p className="login-subtitle">Entre com a sua conta</p>
                  <span className="login-description">
                    Digite abaixo o seu CPF e sua senha para entrar
                  </span>
                  <hr className="login-divider" />
                </Col>
              </Row>

              <div className="login-fields">
                <Row className="login-field-row">
                  <Col className="loginField">
                    <InputText
                      name="document"
                      label={
                        <div className="login-label">
                          <IdCard className="mr-2" />
                          <span>Seu CPF</span>
                        </div>
                      }
                      placeholder="000.000.000-00"
                      maxLength={14}
                      value={formData[FormInputName.document]}
                      onChange={e => onChangeFormInput(FormInputName.document)(e.target.value)}
                      onBlur={e => onCheckUserType(e.target.value)}
                      error={formErrors.document && formErrors.document[0]}
                    />
                  </Col>
                </Row>

                {shouldShowUserType && (
                  <Row className="login-field-row">
                    <Col>
                      <SelectCustom
                        name="userType"
                        label="Perfil"
                        placeholder="Digite ou selecione o perfil"
                        onChange={e => {
                          onChangeFormInput(FormInputName.userType)(e?.value as string);
                        }}
                        error={formErrors.userType && formErrors.userType[0]}
                        value={formData[FormInputName.userType]}
                        options={types}
                      />
                    </Col>
                  </Row>
                )}

                <Row className="login-field-row">
                  <Col>
                    <div className="password-input-wrapper">
                      <InputText
                        name="password"
                        label={
                          <div className="login-label">
                            <LoginLock className="mr-2" />
                            <span>Senha</span>
                          </div>
                        }
                        type={shouldShowPasswordToText ? 'text' : 'password'}
                        placeholder="Digite sua senha"
                        value={formData[FormInputName.password]}
                        maxLength={15}
                        onChange={e => onChangeFormInput(FormInputName.password)(e.target.value)}
                        error={formErrors.password && formErrors.password[0]}
                        renderForward={
                          <div className="password-toggle-inside" onClick={onTogglePasswordToText}>
                            <img
                              className="passwordIcon"
                              src={shouldShowPasswordToText ? eye : closeEye}
                              alt={shouldShowPasswordToText ? 'Ocultar senha' : 'Mostrar senha'}
                            />
                          </div>
                        }
                      />
                    </div>
                  </Col>
                </Row>
              </div>

              <Row className="login-field-row">
                <Col>
                  <Button
                    title="Entrar"
                    type="submit"
                    disabled={isSubmitDisabled}
                    onClick={onSubmit}
                    theme="blue"
                    size="xl"
                    className="login-button-modern"
                  />
                </Col>
              </Row>

              <div className="forgotPassword">
                <button type="button" onClick={onGoToForgotPassword} className="forgotPasswordLink">
                  Esqueceu a sua senha?
                </button>
              </div>
            </Form>
          </Col>
          {/* </Row> */}
        </Container>
      </div>
    </>
  );
};
